import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";

interface PhotoSlide {
  id: string;
  image_url: string;
  alt_text: string;
  caption_en: string;
  caption_ps: string;
  caption_da: string;
  sort_order: number;
  is_active: boolean;
}

function getCaption(photo: PhotoSlide, lang: string): string {
  if (lang === "ps") return photo.caption_ps || photo.caption_en;
  if (lang === "da") return photo.caption_da || photo.caption_en;
  return photo.caption_en;
}

export default function PhotoSlider() {
  const { t, i18n } = useTranslation();
  const [photos, setPhotos] = useState<PhotoSlide[]>([]);
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function loadPhotos() {
      try {
        const { data, error: fetchErr } = await supabase
          .from("photo_slider")
          .select("*")
          .eq("is_active", true)
          .order("sort_order", { ascending: true });

        if (fetchErr) throw fetchErr;

        if (data && data.length > 0) {
          setPhotos(data as PhotoSlide[]);
        }
      } catch (err: any) {
        setError(err?.message || "Failed to load photos");
      } finally {
        setLoading(false);
      }
    }

    loadPhotos();
  }, []);

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning || photos.length === 0) return;
      setIsTransitioning(true);
      setCurrent(index);
      setTimeout(() => setIsTransitioning(false), 500);
    },
    [isTransitioning, photos.length]
  );

  const goNext = useCallback(() => {
    if (photos.length === 0) return;
    goTo((current + 1) % photos.length);
  }, [current, photos.length, goTo]);

  const goPrev = useCallback(() => {
    if (photos.length === 0) return;
    goTo((current - 1 + photos.length) % photos.length);
  }, [current, photos.length, goTo]);

  useEffect(() => {
    if (photos.length <= 1) return;
    intervalRef.current = setInterval(goNext, 6000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [goNext, photos.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    if (headerRef.current) {
      headerRef.current.classList.add("reveal");
      observer.observe(headerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  if (loading) {
    return (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-500 mb-3 block">
              {t("photoSlider.tag")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4 font-heading">
              {t("photoSlider.title")}
            </h2>
          </div>
          <div className="relative rounded-2xl overflow-hidden bg-[#FAF8F5] aspect-[2/1] md:aspect-[16/7] flex items-center justify-center">
            <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-500 mb-3 block">
              {t("photoSlider.tag")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4 font-heading">
              {t("photoSlider.title")}
            </h2>
          </div>
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-red-50">
            <i className="ri-image-line text-2xl text-red-400"></i>
          </div>
          <p className="text-sm text-foreground-600 font-medium mb-1">{t("photoSlider.errorTitle")}</p>
          <p className="text-xs text-foreground-500 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3B9EF5] text-[#1B2838] rounded-full font-semibold text-sm cursor-pointer hover:bg-[#2A8DE0] transition-colors whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i> {t("common.retry")}
          </button>
        </div>
      </section>
    );
  }

  if (photos.length === 0) {
    return (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-500 mb-3 block">
              {t("photoSlider.tag")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4 font-heading">
              {t("photoSlider.title")}
            </h2>
          </div>
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-[#3B9EF5]/10">
            <i className="ri-image-line text-2xl text-secondary-500"></i>
          </div>
          <p className="text-sm text-foreground-600">{t("photoSlider.empty")}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10" ref={headerRef}>
          <span className="text-xs font-semibold uppercase tracking-widest text-primary-500 mb-3 block">
            {t("photoSlider.tag")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4 font-heading">
            {t("photoSlider.title")}
          </h2>
          <p className="text-foreground-600 text-base max-w-xl mx-auto">
            {t("photoSlider.subtitle")}
          </p>
        </div>

        <div className="relative rounded-2xl overflow-hidden group">
          {/* Main image */}
          <div className="relative w-full aspect-[2/1] md:aspect-[16/7] bg-[#1B2838]">
            {photos.map((photo, index) => (
              <div
                key={photo.id}
                className={`absolute inset-0 transition-opacity duration-500 ${
                  index === current ? "opacity-100 z-10" : "opacity-0 z-0"
                }`}
              >
                <img
                  src={photo.image_url}
                  alt={photo.alt_text}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
              </div>
            ))}

            {/* Caption */}
            <div className="absolute bottom-0 left-0 right-0 z-20 p-6 md:p-10">
              <p className="text-white text-base md:text-lg font-medium max-w-2xl drop-shadow-md">
                {photos[current] && getCaption(photos[current], i18n.language)}
              </p>
            </div>

            {/* Counter */}
            <div className="absolute top-6 right-6 z-20 bg-black/40 backdrop-blur-sm rounded-full px-4 py-1.5 text-white text-sm font-medium">
              {current + 1} / {photos.length}
            </div>
          </div>

          {/* Arrows */}
          <button
            onClick={goPrev}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/90 hover:bg-white text-foreground-950 flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-lg whitespace-nowrap"
            aria-label="Previous photo"
          >
            <i className="ri-arrow-left-s-line text-xl md:text-2xl"></i>
          </button>
          <button
            onClick={goNext}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/90 hover:bg-white text-foreground-950 flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-lg whitespace-nowrap"
            aria-label="Next photo"
          >
            <i className="ri-arrow-right-s-line text-xl md:text-2xl"></i>
          </button>

          {/* Dots */}
          <div className="absolute bottom-20 md:bottom-24 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
            {photos.map((_, index) => (
              <button
                key={index}
                onClick={() => goTo(index)}
                className={`rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  index === current
                    ? "w-8 h-2.5 bg-white"
                    : "w-2.5 h-2.5 bg-white/50 hover:bg-white/80"
                }`}
                aria-label={`Go to photo ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}